import { TZDate } from "@date-fns/tz";

import { ROUND_TITLE_BY_ROUND_TYPE } from "app/data/game-settings";
import { RoundType } from "app/game-data/useFetchGameData";
import useGameData from "app/game-data/useGameData";
import NewsletterSignupBanner from "app/game-over/NewsletterSignupBanner";
import NPSFeedbackDialog from "app/game-over/feedback/NPSFeedbackDialog";
import cn from "app/utils/classnames";
import GameStreakModal from "app/game-over/GameStreakModal";
import ShareButton from "app/game-over/ShareButton";
import { GameAction } from "app/game/gameState";
import { useGameActionDispatch, useGameState } from "app/game/useGame";
import type { SpellingData } from "app/score/scoreState";
import { useScoreState } from "app/score/useScore";
import ExitGameButton from "app/top-nav/ExitGameButton";
import CountDownTimer from "app/ui/CountDownTimer";
import Header from "app/ui/Header";
import VideoEmbed from "app/ui/VideoEmbed";
import WordSpellingRecord from "app/ui/WordSpellingRecord";
import useVisitCounter from "app/utils/useVisitCounter";
import mixpanel from "mixpanel-browser";
import { useEffect, useRef, useState } from "react";
import MoreGamesButton from "app/game-over/MoreGamesButton";
import { useFetchTedTalkMetadata } from "app/utils/useFetchTedTalkMetadata";
import extractTedVideoSlug from "app/utils/extractTedVideoSlug";

const SAILTHRU_CUSTOMER_ID = import.meta.env.VITE_SAILTHRU_CUSTOMER_ID;

export default function GameOver() {
  const { gameStreakComplete, id: gameId } = useGameState();
  const gameActionDispatch = useGameActionDispatch();
  const today = new TZDate(new Date(), "America/New_York");
  const gameData = useGameData();
  const scoreState = useScoreState();
  const spellingContainerRef = useRef<HTMLUListElement>(null);
  const [spellingsExpanded, setSpellingsExpanded] = useState(false);
  const tedWordRounds =
    gameData?.levels?.filter((r) => r.levelType === RoundType.TED_WORD) ?? [];
  const tedWordVideoSlug = extractTedVideoSlug(
    tedWordRounds.length > 0 ? (tedWordRounds[0].videoUrl ?? "") : "",
  );
  const {
    title: tedVideoTitle,
    speaker: tedVideoSpeaker,
    viewedCount: tedVideoViewCount,
  } = useFetchTedTalkMetadata(tedWordVideoSlug);

  const { visitCount, reachedLimit } = useVisitCounter({
    storeKey: "SHARE_PAGE_GAME_VISITS",
    maxVisits: 4,
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.Sailthru?.init({
      customerId: SAILTHRU_CUSTOMER_ID,
      hostWebsiteType: "spa",
      overlayKeyValue: {
        "share-page": "true",
        visit: reachedLimit ? `4+` : `${visitCount}`,
        "spellcheck-banner": visitCount < 3 ? "true" : "false",
      },
    });
  }, [reachedLimit, visitCount]);

  useEffect(() => {
    let timeout = undefined;
    if (spellingsExpanded) {
      timeout = setTimeout(() => {
        spellingContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [spellingsExpanded]);

  useEffect(() => {
    mixpanel.track_pageview({
      page: `Game Over Screen`,
      game_id: gameData?.id,
    });
  }, []);

  if (!gameStreakComplete) {
    return (
      <GameStreakModal
        onClose={() => {
          gameActionDispatch({
            type: GameAction.GAME_STREAK_COMPLETED,
          });
        }}
      />
    );
  }

  return (
    <div className={cn("relative z-10 flex h-dvh flex-col")}>
      <ExitGameButton alwaysShowText />
      <div
        className={cn(
          "py-4 px-2 md:pb-0 md:pt-1",
          "text-center",
          "basis-full grow",
          "flex flex-col items-center justify-start",
        )}
      >
        <div
          className={cn(
            "w-full max-w-150 min-h-full",
            "flex flex-col items-center gap-4 md:gap-3",
          )}
        >
          <Header className={cn("w-full md:w-auto", "md:px-10")}>
            Results
          </Header>
          {tedWordVideoSlug && (
            <div className="flex flex-col gap-1 w-full max-w-72 md:max-w-90">
              <div className="w-fit md:w-full m-auto max-h-50 md:max-h-75 overflow-hidden flex items-center justify-center">
                <VideoEmbed
                  className="w-full aspect-video h-auto object-contain"
                  autoplay={false}
                  muted={false}
                  slug={tedWordVideoSlug}
                  subtitles
                  showDetails
                />
              </div>

              <div className="inline-block text-left px-1">
                <a
                  className={cn(
                    "underline decoration-1 underline-offset-1.5",
                    "inline-block leading-[1.2]",
                    "text-black hover:text-gray-500",
                  )}
                  href={`https://www.ted.com/talks/${tedWordVideoSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {tedVideoTitle ?? "Learn more on TED."}
                </a>
                <p className="text-sm">
                  {`${formatVideoViews(tedVideoViewCount)} plays | ${tedVideoSpeaker}`}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col items-center gap-3">
            <p className={cn("text-lg font-semibold", "mt-1")}>
              Total score: {scoreState.gameScore.toLocaleString()}
            </p>
            <div className="flex flex-col md:flex-row gap-3">
              <ShareButton />
              <MoreGamesButton className="md:hidden" />
            </div>
            <CountDownTimer
              className="text-sm"
              text="New game in"
              targetDate={new Date(today.setHours(24, 0, 0, 0))}
            />
            <NPSFeedbackDialog id={gameId} />
            <NewsletterSignupBanner />
          </div>

          <button // SPELLING SECTION
            className={cn(
              "flex flex-col items-center gap-1",
              "w-full mt-2 px-1 py-2",
              "border border-gray-300",
            )}
            onClick={() => {
              setSpellingsExpanded(!spellingsExpanded);
            }}
          >
            <h2 className={cn("font-semibold text-xl", "mb-1")}>
              Your Spelling
            </h2>
            <ul // Spelling holder
              ref={spellingContainerRef}
              className={cn(
                "transition-all duration-500",
                spellingsExpanded ? "max-h-5000" : "max-h-0",
                spellingsExpanded ? "mb-1" : "mb-0",
                spellingsExpanded ? "border-b-white" : "border-b-gray-300",
                spellingsExpanded ? "w-full" : "w-[97%]",
                "border-b",
                "overflow-hidden",
                "transition-all duration-200",
                "flex flex-col gap-2",
              )}
            >
              {scoreState.roundScores.map((roundScore, i) => {
                if (!scoreState.roundScores[i]) {
                  return <></>;
                }

                const roundTitle =
                  ROUND_TITLE_BY_ROUND_TYPE[
                    gameData?.levels?.[i]?.levelType ?? 0
                  ];

                return (
                  <RoundScoreList
                    className={cn(
                      spellingsExpanded ? "opacity-100" : "opacity-0",
                    )}
                    key={`${roundTitle}${i}`}
                    roundNumber={i + 1}
                    roundTitle={roundTitle}
                    score={scoreState.roundScores[i].score}
                    spellings={roundScore.wordSpellings}
                  />
                );
              })}
            </ul>
            <div // Expand/close caption
              className={cn(
                "text-sm uppercase text-gray-700",
                "flex justify-center items-baseline gap-1",
                "-mt-0.5",
                "cursor-pointer",
              )}
            >
              {spellingsExpanded ? (
                <>
                  <p>Close</p>
                  <p className="text-xl font-extralight">-</p>
                </>
              ) : (
                <>
                  <p>Expand</p>
                  <p className="text-xl font-extralight">+</p>
                </>
              )}
            </div>
          </button>
        </div>
      </div>
      <div className="hidden w-full bg-white md:mt-0 md:block md:pt-4 md:pb-8">
        <all-games></all-games>
      </div>
    </div>
  );
}

interface RoundScoreListProps {
  roundNumber: number;
  roundTitle: string;
  score: number;
  spellings: Array<SpellingData>;
  className?: string;
}

function RoundScoreList({
  roundNumber,
  roundTitle,
  score,
  spellings,
  className = "",
}: RoundScoreListProps): React.ReactNode {
  return (
    <div
      key={roundNumber}
      className={cn(
        "w-full",
        // "border border-gray-300",
        "px-2 pt-1 pb-2",
        "transition-all duration-200",
        className,
      )}
    >
      <div
        className={cn(
          "flex justify-between",
          "font-semibold text-sm",
          "mb-1.5",
          "pointer-events-none",
        )}
      >
        <h3 className="text-md md:text-lg font-semibold md:font-normal">
          Round {roundNumber}: {roundTitle}
        </h3>
        <p className="text-md md:text-lg font-semibold md:font-normal">
          Score: {score}
        </p>
      </div>
      <div className={cn("flex flex-col")}>
        <WordSpellingRecord
          numTotalWords={spellings.length}
          completedSpellings={spellings}
          hideScore={roundTitle.toLowerCase() === "speed"}
        />
      </div>
    </div>
  );
}

function formatVideoViews(views: number): string {
  if (views >= 1000000) {
    return (views / 1000000).toFixed(1) + "M";
  }
  return views.toLocaleString();
}
